// The paused page (Accelerator.dc.html, its "Access suspended" Tweak; docs/data-model.md 3.2), in
// the product's own look. Suspended: 7 days after an unpaid invoice's due date, with Update card,
// which opens Stripe's card form for this product's billing, and it opens again by itself once the
// payment lands; on a place someone else's subscription pays for (a partner seat, billing/payer.js), the
// payer's payment and no Update card. Paused: paused in Stripe or by Adam, until paused_until, with no card
// to update.
// Nothing in the product opens from here; everything else they've joined stays open. A mastermind's
// paused page has no product menu, since every item would open this same card (the Accelerator
// critique's member 13, 20 Oct): Back to your library is the way on. Every other type keeps its menu.
import * as f from '../lib/format.js'
import { Link } from '../lib/router.jsx'
import { useNow } from '../lib/clock.js'
import { LockIcon } from '../shell/Marks.jsx'
import { useUpdateCard } from '../shell/PaymentBanner.jsx'
import { ProductFrame, frameChip } from './ProductFrame.jsx'
import { paidByOther, payerPayment, placeWord } from '../billing/payer.js'

// What's paused, and what waits for them, in each type's words. The Accelerator's are its board's.
export function pausedWords(product) {
  switch (product.type) {
    case 'mastermind': return { subject: 'the members area', verb: 'is', kept: 'with your goal, actions and recordings where you left them' }
    case 'masterclass': return { subject: product.name, verb: 'is', kept: 'right where you left it' }
    case 'resource_pack': return { subject: 'your resources', verb: 'are', kept: 'just as they were' }
    case 'client_space': return { subject: 'your space', verb: 'is', kept: 'with everything where you left it' }
    default: return { subject: 'the course', verb: 'is', kept: 'with your recordings and progress where you left them' }
  }
}

const capital = s => s.charAt(0).toUpperCase() + s.slice(1)

// "Friday 1 January", with the year when it isn't this year's
function untilDate(day, nowMs) {
  const d = f.calendarDay(day)
  return f.longDate(d) + (d.y !== f.uk(nowMs).y ? ` ${d.y}` : '')
}

export function pausedLine(product, access, nowMs) {
  const w = pausedWords(product)
  if (access.status === 'suspended') {
    const on = access.payment_failed_at ? ` on ${f.dayMonthLong(access.payment_failed_at)}` : ''
    const whose = paidByOther(access) ? payerPayment(access, placeWord(product)) : 'Your payment'
    return `${whose}${on} didn't go through, so ${w.subject} ${w.verb} paused. It opens again as soon as the payment goes through, ${w.kept}.`
  }
  if (access.paused_until) {
    return `${capital(w.subject)} ${w.verb} paused until ${untilDate(access.paused_until, nowMs)}. It opens again then, ${w.kept}.`
  }
  return `${capital(w.subject)} ${w.verb} paused. It opens again when the pause ends, ${w.kept}.`
}

export default function PausedPage({ product, owned }) {
  const nowMs = useNow()
  const access = owned.access
  const suspended = access.status === 'suspended'
  // Update card only for their own payment: never on a place someone else pays for
  const cardToUpdate = suspended && !paidByOther(access)
  const target = { productId: product.id, hasCustomer: owned.rows.some(r => r.has_customer) }
  const card = useUpdateCard(target)
  const chip = frameChip(product, owned)

  return (
    <ProductFrame product={product} chip={chip} menu={product.type === 'mastermind' ? false : undefined}>
      <main className="pp-paused">
        <div className="pp-paused-card">
          <span className="pp-paused-lock"><LockIcon size={36} /></span>
          <h1 className="pp-paused-h1">Your access is paused</h1>
          <p className="pp-paused-text">{pausedLine(product, access, nowMs)}</p>
          <div className="pp-paused-actions">
            {cardToUpdate && (
              <Link to="/account" className="pp-btn" onClick={card.onClick} aria-busy={card.state === 'opening' || undefined}>Update card</Link>
            )}
            <Link to="/" className="pp-btn pp-btn--line">Back to your library</Link>
          </div>
          {/* Stripe's page didn't open, or staff are viewing as a member: useUpdateCard's own line */}
          {card.message && <p className="pp-paused-msg" role="alert">{card.message}</p>}
          <p className="pp-paused-note">Everything else you've joined is still open in your library.</p>
        </div>
      </main>
    </ProductFrame>
  )
}
